import React, { useState, useEffect } from 'react'
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/dashboard.css';
import '../../assets/styles/myaccount.css';
import '../../assets/styles/subusers.css';
import commonService from '../../services/otherServices';
import Pagination from '../../components/Common/Pagination';
import Popup from '../../components/Common/Error'

const SubUsers = () => {
    const [subUsers, setSubUsers] = useState<any[]>([]);
    const [isListLoading, setIsListLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });

    const [pageDetail, setPageDetail] = useState({
        page: 1,
        total_pages: 1,
        total_records: 0,
        page_size: 10,
    });

    const loadPage = async (page: number) => {
        if (isListLoading) return;
        try {
            setIsListLoading(true);
            const response = await commonService.listSubUsers({ page, page_size: pageDetail.page_size });
            setSubUsers(response?.data?.items || []);
            setPageDetail({
                page: response.data.page,
                total_pages: response.data.total_pages,
                total_records: response.data.total,
                page_size: response.data.page_size,
            });
        } catch (error) {
            console.error("Error fetching sub-users:", error);
        } finally {
            setIsListLoading(false);
        }
    };

    useEffect(() => {
        loadPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddSubUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMsg("");
        try {
            await commonService.createSubUser(form);
            setForm({ first_name: "", last_name: "", email: "" });
            setSuccessMsg("Sub-user added. They'll receive an email to set their password.");
            loadPage(1);
        } catch (err: any) {
            setErrorMsg(err?.response?.data?.message || "Failed to add sub-user.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleStatus = async (item: any) => {
        try {
            if (item.account_status === "active") {
                await commonService.deactivateSubUser(item.id);
            } else {
                await commonService.activateSubUser(item.id);
            }
            loadPage(pageDetail.page);
        } catch (err) {
            setErrorMsg("Failed to update sub-user status.");
        }
    };

    const removeSubUser = async (item: any) => {
        if (!window.confirm(`Remove ${item.email}? This cannot be undone.`)) return;
        try {
            await commonService.deleteSubUser(item.id);
            loadPage(pageDetail.page);
        } catch (err) {
            setErrorMsg("Failed to remove sub-user.");
        }
    };

    return (
        <div className="container subuser-page-wrapper">
            <p className="your-data">Sub Users</p>
            <p className="track">Add sub-users to your account - they can view and download the same data you can.</p>

            <div className="subuser-add-panel">
                <form onSubmit={handleAddSubUser} className="row">
                    <div className="col-md-3">
                        <label htmlFor="sub_first_name" className="my-name">First Name *</label>
                        <input
                            id="sub_first_name"
                            type="text"
                            className="my-input"
                            required
                            maxLength={50}
                            value={form.first_name}
                            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        />
                    </div>
                    <div className="col-md-3">
                        <label htmlFor="sub_last_name" className="my-name">Last Name</label>
                        <input
                            id="sub_last_name"
                            type="text"
                            className="my-input"
                            maxLength={50}
                            value={form.last_name}
                            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="sub_email" className="my-name">Email Address *</label>
                        <input
                            id="sub_email"
                            type="email"
                            className="my-input"
                            required
                            maxLength={255}
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button type="submit" className="save-and-update" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Adding...
                                </>
                            ) : "Add Sub User"}
                        </button>
                    </div>
                </form>

                {successMsg && (
                    <p className="track" style={{ color: "#0CAF60", marginTop: "12px", marginBottom: 0 }}>
                        {successMsg}
                    </p>
                )}
            </div>

            <div className="subuser-table-wrapper">
                <table className="subuser-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Added On</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isListLoading && (
                            <tr>
                                <td colSpan={5} className="text-center py-3">
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Loading...
                                </td>
                            </tr>
                        )}
                        {!isListLoading && subUsers.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center py-3">
                                    No sub-users added yet.
                                </td>
                            </tr>
                        )}
                        {subUsers.map((item) => (
                            <tr key={item.id}>
                                <td>{item.first_name} {item.last_name || ""}</td>
                                <td>{item.email}</td>
                                <td>
                                    <span className={`subuser-status-badge ${item.account_status === "active" ? "active" : "inactive"}`}>
                                        {item.account_status}
                                    </span>
                                </td>
                                <td>{item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : '--'}</td>
                                <td>
                                    {item.account_status === "active" ? (
                                        <button type="button" className="subuser-deactivate" onClick={() => toggleStatus(item)}>
                                            Deactivate
                                        </button>
                                    ) : (
                                        <button type="button" className="subuser-activate" onClick={() => toggleStatus(item)}>
                                            Activate
                                        </button>
                                    )}
                                    <button type="button" className="delete-profile" onClick={() => removeSubUser(item)}>
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="dashboard-pagination-wrapper">
                <Pagination
                    page={pageDetail.page}
                    total_pages={pageDetail.total_pages}
                    total_records={pageDetail.total_records}
                    page_size={pageDetail.page_size}
                    onPageChange={(newPage) => loadPage(newPage)}
                />
            </div>

            <Popup
                isOpen={!!errorMsg}
                onClose={() => setErrorMsg("")}
                message={errorMsg}
            />
        </div>
    );
};

export default SubUsers;
